// The opponents.
//
// One brain, greedy over the things the rules actually pay for. For a normal
// explore card the bot tries every legal placement of every offered shape and
// terrain and takes the one that gains the most: the current season's two
// edicts at full weight, the other two at a discount (they score in seasons
// to come - Spring's B card scores again in Summer), coins at face value
// (a coin pays out once per remaining season), and a nudge toward walling in
// mountains. For an ambush the bot is drawing on a NEIGHBOUR'S map (rules
// p7) and simply maximises the harm: empty spaces newly poisoned by the
// monster, minus anything the placement would accidentally score for the
// victim.
//
// Ties break on the seeded RNG, so a replayed seed replays the bots too.

import {
  placementsOf, draw, MOUNTAINS, neighbours, isFilled, SIZE,
} from '../board';
import { optionsForTurn, drawerOfMap } from '../game';
import { scoreEdict, monsterPenalty } from '../scoring';
import { nextRandom } from '../rng/rng';

export const BOT_NAMES = ['Gimnax', 'Dragul', 'Inkwell', 'Quill', 'Vellum'];

// Seasons a coin banked now will still be paid for, given the current one.
const coinValue = (seasonIndex) => 4 - seasonIndex;

const mountainProgress = (gridBefore, gridAfter) => {
  let progress = 0;
  MOUNTAINS.forEach(([mr, mc]) => {
    neighbours(mr, mc).forEach(([r, c]) => {
      if (!isFilled(gridBefore, r, c) && gridAfter[r][c] !== '.') progress += 1;
    });
  });
  return progress;
};

const candidatesFor = (grid, turn) => {
  const out = [];
  optionsForTurn(turn, grid).forEach((option) => {
    const spots = option.fallback
      ? placementsOf(grid, [[0, 0]])
      : placementsOf(grid, option.cells, {
        needsRuin: Boolean(turn.ruins && turn.type === 'explore'),
      });
    spots.forEach((cells) => {
      option.terrains.forEach((terrain) => {
        out.push({
          cells, terrain, fallback: option.fallback, coin: option.coin,
        });
      });
    });
  });
  return out;
};

const scoreOwnCandidate = (grid, candidate, script, seasonIndex) => {
  const [after, mountainCoins] = draw(grid, candidate.cells, candidate.terrain);
  let value = 0;
  ['A', 'B', 'C', 'D'].forEach((letter) => {
    // Each edict scores in exactly two seasons. Full weight when it scores
    // THIS season, a discount for each later scoring still to come, nothing
    // once its seasons are spent (Winter's B and C deltas are worthless).
    const activeNow = script.seasons[seasonIndex].edicts.includes(letter);
    const later = script.seasons
      .slice(seasonIndex + 1)
      .filter((s) => s.edicts.includes(letter)).length;
    const weight = (activeNow ? 1 : 0) + 0.35 * later;
    if (weight === 0) return;
    const key = script.edicts[letter];
    value += weight * (scoreEdict(key, after) - scoreEdict(key, grid));
  });
  value += (mountainCoins + (candidate.coin ? 1 : 0)) * coinValue(seasonIndex);
  value += 0.15 * mountainProgress(grid, after);
  if (candidate.terrain === 'monster') {
    value -= (monsterPenalty(after) - monsterPenalty(grid));
  }
  return value;
};

const scoreAmbushCandidate = (grid, candidate, script, seasonIndex) => {
  const [after] = draw(grid, candidate.cells, candidate.terrain);
  let harm = monsterPenalty(after) - monsterPenalty(grid);
  script.seasons[seasonIndex].edicts.forEach((letter) => {
    const key = script.edicts[letter];
    harm -= 0.5 * (scoreEdict(key, after) - scoreEdict(key, grid));
  });
  // Mid-map monsters age worse for the victim than edge ones.
  const centre = (SIZE - 1) / 2;
  const spread = candidate.cells.reduce(
    (sum, [r, c]) => sum + Math.abs(r - centre) + Math.abs(c - centre),
    0,
  ) / candidate.cells.length;
  return harm - 0.05 * spread;
};

/**
 * The bot's drawing for one map on one turn. `seat` is the bot's own seat;
 * on ambush turns the grid handed in must already be the victim's map
 * (`mapDrawnBy(turn, seat, n)`). Returns { placement, rng } - the RNG state
 * threads through so bot play stays reproducible.
 */
export const choosePlacement = ({ grid, turn, script, rng, forOwnMap = true }) => {
  const candidates = candidatesFor(grid, turn);
  if (!candidates.length) return { placement: null, rng };

  const seasonIndex = turn.season;
  let state = rng;
  let best = null;
  let bestValue = -Infinity;
  candidates.forEach((candidate) => {
    const value = forOwnMap && turn.type !== 'ambush'
      ? scoreOwnCandidate(grid, candidate, script, seasonIndex)
      : scoreAmbushCandidate(grid, candidate, script, seasonIndex);
    let noise;
    [noise, state] = nextRandom(state);
    const jittered = value + noise * 0.01;
    if (jittered > bestValue) {
      bestValue = jittered;
      best = candidate;
    }
  });

  return {
    placement: { cells: best.cells, terrain: best.terrain, fallback: best.fallback },
    rng: state,
  };
};

export { drawerOfMap };
